import { faClose } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  useFacility,
  useFacilityCategories,
  useQueryKisiGroupMappings,
} from "@gymflow/api";
import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import useGymflowModels from "../../../store";
import { SlideSideBar } from "../../atoms";
import { FacilityForm } from "./FacilityForm";
import { FacilitySideBarContext } from "./FacilitySideBarContext";

export function FacilitySideBarFormProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const [sidebarState, setSidebarState] = useState<{
    isOpen: boolean;
    facilityId?: number;
  }>({
    isOpen: false,
  });
  const { api } = useGymflowModels();
  const facilityQuery = useFacility({
    api,
    facilityId: sidebarState.facilityId,
  });
  const facilityCategoriesQuery = useFacilityCategories({ api });
  const currentFacilityCategory = facilityCategoriesQuery.data?.find(
    (category) => category.id === facilityQuery.data?.facilityCategoryId,
  );
  const kisiMappingQuery = useQueryKisiGroupMappings({
    api,
  });
  const currentKisiMapping = kisiMappingQuery.data?.find(
    (access) =>
      access.type === "FACILITY" && access.id === facilityQuery.data?.id,
  );
  const isLoading =
    facilityQuery.isLoading ||
    facilityCategoriesQuery.isLoading ||
    kisiMappingQuery.isLoading;
  return (
    <FacilitySideBarContext.Provider
      value={{
        showFacilitySidebar: (facilityId?: number) => {
          setSidebarState({ isOpen: true, facilityId: facilityId });
        },
      }}
    >
      <SlideSideBar
        isOpen={sidebarState.isOpen}
        hide={() => {
          setSidebarState({ isOpen: false });
        }}
        isLoading={isLoading}
      >
        <div className="flex h-full max-h-full flex-col overflow-hidden">
          <div className="flex h-full max-h-full flex-col overflow-hidden">
            <div className="flex flex-col justify-between border-b border-gray-200 p-5">
              <div className="mb-1 flex flex-row items-center justify-between">
                <div className="text-xl font-semibold text-gray-900">
                  {sidebarState.facilityId
                    ? t("settings.facilitySidebar.editTitle")
                    : t("settings.facilitySidebar.createTitle")}
                </div>

                <FontAwesomeIcon
                  onClick={() => {
                    setSidebarState({ isOpen: false });
                  }}
                  className="cursor-pointer text-xl text-gray-600"
                  icon={faClose}
                />
              </div>
              <div className="text-sm font-medium text-gray-600">
                {sidebarState.facilityId
                  ? t("settings.facilitySidebar.editDescription")
                  : t("settings.facilitySidebar.createDescription")}
              </div>
            </div>
            {sidebarState.isOpen && !sidebarState.facilityId && (
              <FacilityForm
                type="create"
                className="h-full"
                onClose={() => {
                  setSidebarState({ isOpen: false });
                }}
              />
            )}
            {sidebarState.isOpen &&
              sidebarState.facilityId &&
              !isLoading &&
              facilityQuery.data &&
              currentFacilityCategory && (
                <FacilityForm
                  type="edit"
                  className="h-full"
                  onClose={() => {
                    setSidebarState({ isOpen: false });
                  }}
                  facilityId={sidebarState.facilityId}
                  initialValues={{
                    name: facilityQuery.data.name,
                    description: facilityQuery.data.description,
                    facilityCategory: {
                      id: facilityQuery.data.facilityCategoryId,
                      label: currentFacilityCategory.name,
                      value: currentFacilityCategory,
                    },
                    picture: facilityQuery.data.picture,
                    kisiMapping: currentKisiMapping
                      ? {
                          id: currentKisiMapping?.kisiGroupId,
                          label: currentKisiMapping?.kisiGroupName,
                        }
                      : undefined,
                  }}
                />
              )}
          </div>
        </div>
      </SlideSideBar>
      {children}
    </FacilitySideBarContext.Provider>
  );
}
